import { SyntheticEvent, KeyboardEvent } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { colors } from '@atlaskit/theme';

import { TableHeader } from 'src/components/pageable-table';
import { changeFilterState } from 'src/redux/jira/actions';
import { FilterState, Sort, SORT_ORDER } from 'src/redux/jira/types';
import { BucketState } from 'src/types/state';

import { sortMessages } from './site-issues-sort-link.i18n';
import * as styles from './site-issues-sort-link.styled';

type Props = {
  intl: InjectedIntl;
  label: string;
  field: string;
  isDisabled: boolean;
  width?: string;
  mobileHidden?: boolean;
  dataIssueColumn?: string;
};

const SiteIssuesSortableTableHeader = ({
  intl,
  label,
  field,
  isDisabled,
  width,
  mobileHidden,
  dataIssueColumn,
}: Props) => {
  const filterState = useSelector<BucketState, FilterState>(
    state => state.jira.jiraRepoPage.filterState
  );

  const isActive = field === filterState.sort.field;
  const order = isActive ? filterState.sort.order : SORT_ORDER.DESC;

  const dispatch = useDispatch();

  const inverseOrder =
    isActive && order === SORT_ORDER.DESC ? SORT_ORDER.ASC : SORT_ORDER.DESC;

  const handleSortChange = () =>
    dispatch(
      changeFilterState({
        sort: {
          field,
          order: inverseOrder,
        } as Sort,
      })
    );

  const handleKeyPress = (event: KeyboardEvent) => {
    event.preventDefault();

    if (!isDisabled && event.key === 'Enter') {
      handleSortChange();
    }
  };

  const handleClick = (event: SyntheticEvent) => {
    event.preventDefault();
    handleSortChange();
  };

  const ariaLabel = isActive
    ? intl.formatMessage(sortMessages[order].activeSortLabel, {
        sortLabel: label,
      })
    : undefined;

  const ariaSort = isActive
    ? order === 'ASC'
      ? 'ascending'
      : 'descending'
    : undefined;

  const Icon = order === SORT_ORDER.ASC ? ChevronUpIcon : ChevronDownIcon;

  return (
    <TableHeader
      aria-sort={ariaSort}
      role="columnheader"
      scope="col"
      mobileHidden={!!mobileHidden}
      width={width}
      data-issue-column={dataIssueColumn || null}
    >
      <styles.SortLink
        isDisabled={isDisabled}
        aria-label={ariaLabel}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
      >
        {label}
        <Icon
          primaryColor={isActive ? colors.N500 : colors.N60}
          label={intl.formatMessage(sortMessages[order].iconLabel)}
        />
      </styles.SortLink>
    </TableHeader>
  );
};

export default injectIntl(SiteIssuesSortableTableHeader);
